import { useState, useRef, useEffect, useContext, memo } from 'react'
import useField from 'components/form/useField'
import styled, { css, ThemeContext } from 'styled-components'
import { caption } from 'styles/mixins/textstyles'
import Spacer from 'components/misc/Spacer'
import { required, failWith } from 'components/form/validations'
import { useLocalization } from 'localization'

let stripe

const style = (theme) => {
  const { colors } = theme
  return {
    base: {
      fontSize: '18px',
      fontFamily: '"proxima-nova","Verdana","Gill Sans","Lucida Grande","Arial","Helvetica",sans-serif',
      fontSmoothing: 'antialiased',
      fontWeight: 400,
      iconColor: colors.greyBase,
      color: colors.black,
      '::placeholder': {
        color: colors.greyBase,
      },
      ':focus': {
        iconColor: colors.primaryBase,
      },
    },
    invalid: {
      iconColor: colors.errorBase,
      color: colors.errorBase,
    },
  }
}

const LabelText = styled.label`
  ${(props) => caption(props.theme.colors.black, 'medium')}
  display: block;
  text-align: left;
  color: ${(props) => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: ${(props) => (props.tight ? '.2rem' : '1rem')};
  padding-bottom: 0.1rem;

  ${(props) =>
    props.focused &&
    css`
      color: ${(props) => props.theme.colors.primaryBase};
    `}

  ${(props) =>
    props.error &&
    css`
      color: ${(props) => props.theme.colors.errorBase};
    `}
`

const Label = (props) => {
  const { label, error, value, focused, tight, id } = props
  const text = error || (value && label) || '\u00A0'
  return (
    <LabelText focused={focused} error={error} tight={tight} id={id}>
      {text}
    </LabelText>
  )
}

const CardWrapper = styled.div`
  border-bottom: 1px solid
    ${(props) =>
      props.error
        ? props.theme.colors.errorBase
        : props.focused
          ? props.theme.colors.primaryBase
          : props.theme.colors.greyLight};
`

export const Card = memo(
  (props) => {
    const { t } = useLocalization()
    const { funding: requiredFunding } = props
    const themeContext = useContext(ThemeContext)
    const [focused, setFocused] = useState(false)
    const [empty, setEmpty] = useState(true)
    const [complete, setComplete] = useState(false)
    const [stripeError, setError] = useState(null)
    const [card] = useState(() => {
      if (!stripe) {
        stripe = window.Stripe(import.meta.env.REACT_APP_STRIPE_KEY)
      }
      const elements = stripe.elements({ fonts: [{ cssSrc: 'https://use.typekit.net/qfx7pbm.css' }] })
      return elements.create('card', { style: style(themeContext) })
    })
    const elem = useRef()

    const validate = stripeError ? failWith(stripeError) : required

    const { setValue, error, id } = useField({ name: props.name, validate, defaultValue: null })

    useEffect(() => {
      card.mount(elem.current)
      return () => card.destroy()
    }, [card])

    useEffect(() => {
      card.addEventListener('change', async (event) => {
        setEmpty(event.empty)
        setComplete(event.complete)
        if (!event.complete) {
          // Don't set value here (or will trigger validation too soon)
          // Only set error here unless one already exists (or will trigger validation too soon)
          // Set error as new error or existing error, otherwise null
          setError(error ? (event.error ? event.error.message : error) : null)
        } else if (event.error) {
          // Got an error from the entry
          setError(event.error.message)
        } else {
          setError(null)
          setValue(async () => {
            // When calling setValue with a function, it invokes the function and
            // uses the value that is returned. In this case a promise.
            // when calling setValue with a promise, it waits and uses the resolved value.
            const { token, error: tokenError } = await stripe.createToken(card, { currency: 'usd' })
            if (token && !tokenError) {
              const funding =
                requiredFunding && (typeof requiredFunding === 'string' ? [requiredFunding] : requiredFunding)
              if (funding && !funding.includes(token.card.funding)) {
                const error =
                  funding.length === 1
                    ? `Must be a ${funding[0]} card. This is a ${token.card.funding} card.`
                    : `Cannot be ${token.card.funding} card`
                setValue(null)
                setError(error)
                return Promise.reject(error)
              }
              return token
            } else if (tokenError) {
              return Promise.reject(tokenError)
            } else {
              return null
            }
          })
        }
      })

      card.addEventListener('focus', () => {
        setFocused(true)
      })
      card.addEventListener('blur', () => {
        setFocused(false)
        if (!error && empty) {
          setValue(null)
          setError('Required')
        } else if (!error && !complete) {
          setValue(null)
          setError('Please Complete')
        }
      })

      return () => {
        card.removeAllListeners()
      }
    }, [elem, card, setValue, error, setError, empty, complete, requiredFunding])

    return (
      <CardWrapper focused={focused} error={error}>
        <Label
          id={`${id}-label`}
          focused={focused}
          label={t('dweb_form_stripe_card_info')}
          value={!empty}
          error={error}
          tight={props.tight}
        />
        <Spacer height=".4rem" />
        <div id="stripe-debit-card" ref={elem} />
        <Spacer height=".4rem" />
      </CardWrapper>
    )
  },
  () => true
)
